import { useMemo, useState } from "react";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { DataTable } from "@repo/ui/components/Base/DataTable";
import { layoutDefaults, useColumns } from "./AgendaItemsDataTableColumns";
import { EditAgendaItemSheet } from "./EditAgendaItemSheet";
import { ArchiveConfirmation } from "../dialog/ArchiveConfirmation";
import { useUserStore } from "@/stores/userStore";
import { AgendaItemReviewDialog } from "./AgendaItemReviewDialog";
import { useNavigate } from "react-router-dom";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { KEYS } from "@/lib/constants";
import { useUserColumns } from "@/hooks/useUserColumns";
import { ReoccurringAgendaItemDialog } from "./ReoccurringAgendaItemDialog";
import { useAgendaItemList } from "@/hooks/queryHooks/agendaItemHooks";
import { useActiveTeamIdOrOrg } from "@/hooks/useActiveTeam";
import { agendaItemRepo } from "@/repositories/agendaItemRepo";
import { useMeetingList } from "@/hooks/queryHooks/meetingHooks";

export const AgendaItemsTable = ({ tenantId }: { tenantId: string }) => {
  const userId = useUserStore((s) => s.id);
  const navigate = useNavigate();
  const { data: agendaItems } = useAgendaItemList(
    tenantId,
    useActiveTeamIdOrOrg() ?? ""
  );
  useMeetingList(tenantId, "org"); // make sure meetings are loaded in cache for 'pending' computations

  const { defaultState, saveTableLayout, resetTableLayout } = useUserColumns(
    KEYS.AGENDAITEM_TABLE_COLUMNS,
    layoutDefaults
  );

  const [activeAgendaItemId, setActiveAgendaItemId] = useState<string>();
  const activeAgendaItem = useMemo(
    () => agendaItems?.find((a) => a.id === activeAgendaItemId),
    [activeAgendaItemId, agendaItems]
  );
  const [editSheetOpen, setEditSheetOpen] = useState(false);

  const [reoccurringOpen, setReoccurringOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const handleQuickEdit = (id: string) => {
    setEditSheetOpen(true);
    setActiveAgendaItemId(id);
  };

  const handleReoccurring = (id: string) => {
    setReoccurringOpen(true);
    setActiveAgendaItemId(id);
  };

  const handleArchive = (docId: string) => {
    setArchiveOpen(true);
    setActiveAgendaItemId(docId);
  };

  const handleReview = (id: string) => {
    setReviewOpen(true);
    setActiveAgendaItemId(id);
  };

  const handleView = (id: string) => {
    navigate(new AppUrlPath(tenantId).getAgendaItemPath(`${id}`));
  };

  return (
    <>
      {activeAgendaItemId && (
        <ArchiveConfirmation
          open={archiveOpen}
          setOpen={setArchiveOpen}
          itemId={activeAgendaItemId}
          onSubmit={async (agendaItemId) => {
            await agendaItemRepo.delete(tenantId, agendaItemId);
          }}
        />
      )}

      {activeAgendaItem && (
        <AgendaItemReviewDialog
          open={reviewOpen}
          setOpen={setReviewOpen}
          tenantId={tenantId}
          agendaItem={activeAgendaItem}
        />
      )}

      {activeAgendaItem && (
        <ReoccurringAgendaItemDialog
          tenantId={tenantId}
          agendaItem={activeAgendaItem}
          open={reoccurringOpen}
          setOpen={setReoccurringOpen}
        />
      )}

      {activeAgendaItemId && (
        <EditAgendaItemSheet
          open={editSheetOpen}
          setOpen={setEditSheetOpen}
          tenantId={tenantId}
          agendaItemId={activeAgendaItemId}
        />
      )}

      {!agendaItems && <Spinner />}

      <DataTable
        columns={useColumns({
          userId,
          tenantId,
          handleQuickEdit,
          handleReoccurring,
          handleArchive,
          handleReview,
          handleView,
        })}
        data={agendaItems ?? []}
        includeMainFilter={false}
        userDefaults={defaultState}
        factoryDefaults={layoutDefaults}
        onLayoutSave={saveTableLayout}
        onLayoutReset={resetTableLayout}
      />
    </>
  );
};
